import { useTheme } from '../../theme/ThemeContext';
import { useBreakpoint } from '../../hooks/useWindowWidth';
import Image from '../ui/Image';

const GD = "var(--font-heading-primary)";
const NU = "var(--font-body)";

// onCountryFocus / activeCountry (was onCountryHover): focusing a country is a STICKY
// selection, not a transient hover. The row no longer clears on mouseleave — you have to
// physically leave the row to reach the map, so clearing there made the interaction
// impossible: the pins vanished mid-journey. Focus persists until another country is
// focused or the map's reset control clears it.
export default function DestinationsList({ destinations, onCountryClick, onRegionClick, onCountryFocus, onRegionFocus, activeCountry, activeRegion }) {
  const C = useTheme();
  const { isMobile } = useBreakpoint();

  if (!destinations || destinations.length === 0) {
    return (
      <section style={{ padding: "60px 40px", textAlign: "center" }}>
        <p style={{ fontFamily: NU, fontSize: 16, color: C.grey }}>
          No destinations with listings yet.
        </p>
      </section>
    );
  }

  return (
    <section style={{ padding: isMobile ? "40px 20px" : "60px 40px", maxWidth: 1200, margin: "0 auto" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 40 }}>
        {destinations.map((country) => {
          // Drives the row's persistent highlight. Without it the map stays filtered to
          // a country while the list looks inactive — the user loses track of what the
          // pins belong to.
          const isActive = activeCountry === country.slug;
          return (
          <div key={country.slug} style={{ borderBottom: `1px solid ${C.border}`, paddingBottom: 40 }}>
            {/* Country Header */}
            <div
              onClick={() => onCountryClick?.(country.slug)}
              style={{
                cursor: "pointer",
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: isMobile ? 12 : 24,
                marginBottom: 16,
                transition: "opacity 0.2s, border-color 0.2s, background 0.2s",
                // Focused row stays marked after the cursor leaves for the map.
                borderLeft: `2px solid ${isActive ? C.gold : "transparent"}`,
                paddingLeft: 14,
                marginLeft: -16,
                background: isActive ? "rgba(211,154,33,0.05)" : "transparent",
              }}
              onMouseEnter={() => onCountryFocus?.(country.slug)}
            >
              {/* Image */}
              <div
                style={{
                  width: isMobile ? "100%" : 140,
                  height: isMobile ? 160 : 140,
                  borderRadius: 4,
                  overflow: "hidden",
                  flexShrink: 0,
                  background: C.border,
                }}
              >
                <Image
                  src={country.image}
                  width={500}
                  alt={country.name}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </div>

              {/* Info */}
              <div style={{ flex: 1 }}>
                <h2 style={{ fontFamily: GD, fontSize: isMobile ? 24 : 32, color: C.off, margin: "0 0 10px", fontWeight: 400 }}>
                  {country.name}
                </h2>
                <p style={{
                  fontFamily: NU,
                  fontSize: 14,
                  color: C.grey,
                  lineHeight: 1.6,
                  margin: "0 0 10px",
                  maxWidth: 500,
                }}>
                  {country.aiSummary}
                </p>
                <div style={{ display: "flex", gap: 16 }}>
                  <span style={{ fontFamily: NU, fontSize: 12, color: C.gold }}>
                    {country.venueCount} {country.venueCount === 1 ? "venue" : "venues"}
                  </span>
                  {country.vendorCount > 0 && (
                    <span style={{ fontFamily: NU, fontSize: 12, color: C.gold }}>
                      {country.vendorCount} {country.vendorCount === 1 ? "vendor" : "vendors"}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Region Pills */}
            {country.regions.length > 0 && (
              <div style={{ marginLeft: isMobile ? 0 : 164, display: "flex", flexWrap: "wrap", gap: 8 }}>
                {country.regions.map((region) => {
                  // Same sticky rule as the country row: hovering a region FRAMES it on
                  // the map, clicking still navigates to /[country]/[region]. The gold
                  // state is driven by props rather than by mutating style on
                  // mouseenter/leave, so the pill and the map cannot disagree about
                  // which region is focused.
                  const isRegionActive = isActive && activeRegion === region.slug;
                  return (
                    <button
                      key={region.slug}
                      onClick={() => onRegionClick?.(country.slug, region.slug)}
                      onMouseEnter={() => onRegionFocus?.(country.slug, region.slug)}
                      style={{
                        fontFamily: NU,
                        fontSize: 12,
                        padding: "5px 12px",
                        borderRadius: 2,
                        border: `1px solid ${isRegionActive ? C.gold : C.border}`,
                        background: isRegionActive ? "rgba(211,154,33,0.08)" : "transparent",
                        color: isRegionActive ? C.gold : C.grey,
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                    >
                      {region.name}
                      <span style={{ marginLeft: 6, opacity: 0.6 }}>
                        {region.venueCount + region.vendorCount}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          );
        })}
      </div>
    </section>
  );
}
